import { resolveOrgProjectContext } from "@arcadeai/ui-kit/org-project-context/resolve";
import type {
  OrgProjectFallbackContext,
  OrgProjectResolution,
} from "@arcadeai/ui-kit/org-project-context/types";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import {
  type SessionData,
  sessionQueryOptions,
  useSession,
} from "../auth-client";
import type {
  UseOrgProjectContextResult,
  ValidatedOrgProjectContext,
} from "./constants";
import {
  buildOrgProjectPath,
  getOrgProjectPathSuffix,
  ORG_PROJECT_ROUTE_PATH,
  orgProjectRouteApi,
} from "./constants";
import {
  coordinatorQueries,
  getOrganizationsQueryOptions,
  getProjectsQueryOptions,
} from "./coordinator-query";

function extractScopeKey(session: SessionData | undefined): string | null {
  return session?.user.id ?? null;
}

function getScopeKey(queryClient: QueryClient): string | null {
  return extractScopeKey(
    queryClient.getQueryData<SessionData>(sessionQueryOptions.queryKey)
  );
}

export function prefetchDashboardProjectsForOrg(
  queryClient: QueryClient,
  organizationId: string
) {
  return queryClient.prefetchQuery(
    getProjectsQueryOptions(organizationId, getScopeKey(queryClient))
  );
}

export function resolveDashboardOrgProjectRoute(
  queryClient: QueryClient,
  urlParams?: { organizationId?: string; projectId?: string }
): Promise<OrgProjectResolution> {
  return resolveOrgProjectContext(
    coordinatorQueries.createLoader(queryClient, getScopeKey(queryClient)),
    urlParams
  );
}

export function useOrgProjectRouteContext(): ValidatedOrgProjectContext {
  return orgProjectRouteApi.useRouteContext();
}

export function useScopeKey(): string | null {
  const { data: session } = useSession();
  return extractScopeKey(session);
}

export function useNavigateToScope() {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const navigate = useNavigate({ from: ORG_PROJECT_ROUTE_PATH });

  return useCallback(
    (targetScope: OrgProjectFallbackContext) => {
      const pathname =
        typeof window === "undefined" ? "/" : window.location.pathname;
      const pathnameSuffix = getOrgProjectPathSuffix(pathname, {
        organizationId,
        projectId,
      });

      navigate({
        to: buildOrgProjectPath(pathnameSuffix, targetScope),
        search: (prev: Record<string, unknown>) => prev,
      });
    },
    [navigate, organizationId, projectId]
  );
}

export function useDashboardOrgProject(): UseOrgProjectContextResult {
  const scopeKey = useScopeKey();
  const {
    organizationId: activeOrganizationId,
    projectId: activeProjectId,
    organizations: initialOrganizations,
    projects: initialProjects,
  } = useOrgProjectRouteContext();

  const organizationsQuery = useQuery({
    ...getOrganizationsQueryOptions(scopeKey),
    initialData: initialOrganizations,
  });
  const projectsQuery = useQuery({
    ...getProjectsQueryOptions(activeOrganizationId, scopeKey),
    enabled: Boolean(activeOrganizationId),
    initialData: initialProjects,
  });

  const organizations = organizationsQuery.data ?? [];
  const projects = projectsQuery.data ?? [];
  const activeOrganization =
    organizations.find((item) => item.id === activeOrganizationId) ?? null;
  const activeProject =
    projects.find((item) => item.id === activeProjectId) ?? null;

  return {
    organizations,
    projects,
    activeOrganization,
    activeProject,
    activeOrganizationId,
    activeProjectId,
    isLoadingOrganizations: organizationsQuery.isLoading,
    isLoadingProjects: projectsQuery.isLoading,
    isReady:
      organizationsQuery.isSuccess &&
      (!activeOrganizationId || projectsQuery.isSuccess),
    isError: organizationsQuery.isError || projectsQuery.isError,
  };
}
